import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { FileCode } from "lucide-react";

export default function Declarations() {
  const navigate = useNavigate();
  const { data = [] } = useQuery({ queryKey: ["zzi-subs"], queryFn: async () => (await api.get("/zzi/submissions")).data, refetchInterval: 10000 });
  const { data: cov } = useQuery({ queryKey: ["zzi-coverage"], queryFn: async () => (await api.get("/zzi/coverage")).data });

  return (
    <div className="animate-fade-in">
      <header className="h-14 border-b border-zinc-800 flex items-center px-6">
        <h1 className="font-heading text-lg font-semibold tracking-tight">ZZI Declarations</h1>
        <span className="ml-3 text-xs font-mono text-zinc-500">{data.length} submissions</span>
        {cov?.safe_mode && <span className="ml-auto text-[10px] font-mono px-2 py-1 border border-amber-500/40 text-amber-400 rounded-sm">SAFE MODE</span>}
      </header>
      <div className="p-6 space-y-6">
        {cov?.procedures && (
          <div className="border border-zinc-800 rounded-sm bg-zinc-900/40" data-testid="zzi-coverage">
            <div className="px-3 py-2 border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-400">Procedure coverage</div>
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {cov.procedures.map((p) => (
                <div key={p.code} className="flex items-center gap-2 text-xs border border-zinc-800 rounded-sm px-2 py-1.5">
                  <span className="font-mono text-zinc-200 w-14">{p.code}</span>
                  <span className="text-zinc-400 flex-1 truncate">{p.module}</span>
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${p.live_supported ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>{p.live_supported ? "READY" : "SPEC NEEDED"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-950 text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="text-left font-medium py-2 px-3">Ticket</th>
                <th className="text-left font-medium py-2 px-3">Type</th>
                <th className="text-left font-medium py-2 px-3">MRN</th>
                <th className="text-left font-medium py-2 px-3">Doc State</th>
                <th className="text-left font-medium py-2 px-3">Mode</th>
                <th className="text-left font-medium py-2 px-3">Approved by</th>
                <th className="text-left font-medium py-2 px-3">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {data.map((s) => (
                <tr key={s.id} data-testid={`zzi-row-${s.id}`} onClick={() => navigate(`/tickets/${s.ticket_id}`)}
                  className="border-t border-zinc-800/60 hover:bg-zinc-900 cursor-pointer transition-colors duration-150">
                  <td className="py-2 px-3 font-mono text-blue-400">{s.ticket_id}</td>
                  <td className="py-2 px-3 font-mono text-zinc-300">{s.doc_type?.toUpperCase()}</td>
                  <td className="py-2 px-3 font-mono text-zinc-200">{s.mrn || "—"}</td>
                  <td className="py-2 px-3 font-mono text-emerald-400">{s.doc_state || "—"}</td>
                  <td className="py-2 px-3">
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${s.simulated ? "border-amber-500/40 text-amber-400" : "border-emerald-500/40 text-emerald-400"}`}>
                      {s.simulated ? "SIMULATED" : "LIVE"}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-xs text-zinc-400">{s.approved_by}</td>
                  <td className="py-2 px-3 text-xs font-mono text-zinc-500">{(s.submitted_at || "").slice(0, 19).replace("T", " ")}</td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr><td colSpan={7} className="py-16 text-center text-zinc-500">
                  <FileCode className="h-8 w-8 mx-auto mb-3 text-zinc-700" />
                  <p className="text-sm">No ZZI declarations submitted yet.</p>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
